import type { PlaygroundPageHandle } from '../types.js';
import { PLAYGROUND_GLOBALS } from '../protocol.js';
import { listClients } from './clients.js';
import type { PlaygroundRuntimeContext } from './context.js';
import { clientAt, listAreas, modeOf } from './stepContext.js';

/** A page handle, and how its publisher ends it. */
export interface PageHandleLease {
  readonly handle: PlaygroundPageHandle;
  /** Ends the handle: every read of it throws from now on, naming its playground. */
  release(): void;
}

/**
 * The page handle over a booted playground's runtime context (`PLAYGROUND_GLOBALS.environment`): read-only getters over
 * what its step contexts are built from — so `clients`, `client()` and `areas` read the environment live, and `game`
 * reads whatever the context holds now. Once released, every read throws: a script that kept the handle past its
 * playground's dispose, or past the next boot, would otherwise go on driving an environment that no longer runs.
 */
export const createPageHandle = (ctx: PlaygroundRuntimeContext): PageHandleLease => {
  const { name } = ctx.selection;
  let released = false;
  const live = <T>(read: () => T): T => {
    if (released) {
      throw new Error(
        `[gotti][playground] The page handle of playground "${name}" is no longer live: its playground was disposed, or another boot replaced it. Read window.${PLAYGROUND_GLOBALS.environment} again.`,
      );
    }
    return read();
  };
  const handle: PlaygroundPageHandle = Object.freeze({
    get name() {
      return live(() => name);
    },
    get mode() {
      return live(() => modeOf(ctx.selection));
    },
    get env() {
      return live(() => ctx.environment);
    },
    get clients() {
      return live(() => listClients(ctx.environment));
    },
    client(index = 0) {
      return live(() => clientAt(ctx.environment, index));
    },
    get areas() {
      return live(() => listAreas(ctx.environment));
    },
    get game() {
      return live(() => ctx.game);
    },
    get target() {
      return live(() => ctx.target);
    },
  });
  return {
    handle,
    release: () => {
      released = true;
    },
  };
};
